// Share only in-flight theme reads and never intercept app requests. A completed cart is
// reused only by a caller that names a freshness mark the read was sent after.
(function () {
  window.theme = window.theme || {};
  if (window.theme.cartReads) return;

  // Mutating callers parse their response and dispatch a follow-up event; let that event's
  // read be the only one instead of racing it with a post-mutation read.
  var SETTLE_DELAY = 50;
  // A stalled write (e.g. a hung mobile request) must not block the count, gift check and
  // checkout unlock indefinitely; after this long, reads go out anyway.
  var MUTATION_WAIT = 15000;
  // A hung cart read would hold the gift check (and checkout lock) the same way; fail it
  // instead so callers fall back to their error path and retry.
  var READ_TIMEOUT = 10000;

  var version = 0;
  var clock = 0;
  var pending = null;
  var last = null;
  var mutations = 0;
  var mutationEpoch = 0;
  var mutationWaiters = [];
  var queued = false;

  var listeners = new Set();

  function invalidate() {
    version += 1;
    var previous = pending;
    pending = null;
    // Move existing consumers to the new generation without awaiting the old network request.
    if (previous) previous.resolve(Promise.resolve().then(read));
  }

  // True when the current read was (or will be) sent after `since`, so it is at least as fresh
  // as HTML requested at that mark.
  function isFresh(since) {
    if (pending) return !pending.sentAt || pending.sentAt > since;
    return Boolean(last && last.version === version && last.sentAt > since);
  }

  function isCart(cart) {
    return Boolean(cart && Array.isArray(cart.items) && Number.isInteger(cart.item_count) && cart.item_count >= 0);
  }

  function publish(cart, source) {
    updateBubble(cart);
    document.dispatchEvent(new CustomEvent('cart-reads:state', {
      detail: { cart: cart, version: version, source: source }
    }));
  }

  function updateBubble(cart) {
    var count = String(cart.item_count);
    document.querySelectorAll('.cart-link__bubble').forEach(function (bubble) {
      bubble.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
    });
    document.querySelectorAll('.cart-link__count, .cart-link__bubble-num').forEach(function (node) {
      if (node.textContent !== count) node.textContent = count;
    });
    // Keep the link's accessible name ("Cart, 3") in step with the visible count.
    document.querySelectorAll('.js-drawer-open-cart[aria-label]').forEach(function (link) {
      var base = link.getAttribute('data-cart-label') || link.getAttribute('aria-label').replace(/,\s*\d+$/, '');
      var label = cart.item_count > 0 ? base + ', ' + count : base;
      if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
    });
    listeners.forEach(function (listener) {
      try { listener(cart); } catch (error) { console.error('Cart subscriber failed', error); }
    });
  }

  // Covers the body too: a response can arrive and then stall mid-stream.
  function withReadTimeout(start) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer;
    var timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('Cart read timed out'));
      }, READ_TIMEOUT);
    });
    return Promise.race([start(controller ? controller.signal : undefined), timeout])
      .finally(function () { clearTimeout(timer); });
  }

  async function performRead(record) {
    try {
      var deadline = Date.now() + MUTATION_WAIT;
      while (mutations && Date.now() < deadline) {
        await new Promise(function (resolve) {
          mutationWaiters.push(resolve);
          setTimeout(resolve, deadline - Date.now());
        });
      }
      if (record !== pending) return;
      var route = window.theme.routes && window.theme.routes.cart;
      record.sentAt = ++clock;
      var url = route || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js';
      var cart = await withReadTimeout(function (signal) {
        return fetch(url, {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: signal
        }).then(function (response) {
          if (!response.ok) throw new Error('Cart read failed: ' + response.status);
          return response.json();
        });
      });
      if (!isCart(cart)) throw new Error('Invalid cart response');
      if (record !== pending || record.version !== version) return;
      pending = null;
      last = { cart: cart, version: record.version, sentAt: record.sentAt };
      publish(cart, 'read');
      record.resolve(cart);
    } catch (error) {
      if (record !== pending) return;
      pending = null;
      record.reject(error);
    }
  }

  function read(options) {
    var since = options && options.since;
    if (typeof since === 'number') {
      if (!pending && isFresh(since)) return Promise.resolve(last.cart);
      // A read already on the wire before the mark may predate that HTML; supersede it
      // (its consumers move to the new read too).
      if (pending && !isFresh(since)) invalidate();
    }
    if (!pending) {
      var record = { version: version };
      record.promise = new Promise(function (resolve, reject) {
        record.resolve = resolve;
        record.reject = reject;
      });
      pending = record;
      Promise.resolve().then(function () { return performRead(record); });
    }
    return pending.promise;
  }

  function refresh() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(function () {
      queued = false;
      return read();
    }).catch(function () { /* Preserve the last rendered count; retry on the next event. */ });
  }

  // Settles like `promise`, or rejects after MUTATION_WAIT so callers can restore their UI.
  // A late result still changed the cart: re-read it when it lands.
  function withTimeout(promise, controller) {
    var timer;
    var timedOut = false;
    var timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        timedOut = true;
        if (controller) controller.abort();
        reject(new Error('Cart change timed out'));
      }, MUTATION_WAIT);
    });
    promise.then(function () {
      if (timedOut) { invalidate(); refresh(); }
    }, function () { /* The caller saw the timeout. */ });
    return Promise.race([promise, timeout]).finally(function () { clearTimeout(timer); });
  }

  // options.cart maps the operation's result to the cart it confirms (e.g. change.js JSON).
  // The operation receives an AbortSignal (when supported) that fires if it times out.
  async function mutate(operation, options) {
    var solo = !mutations;
    var epoch = ++mutationEpoch;
    mutations += 1;
    invalidate();
    // The server applied the change after this mark, so its response is at least that fresh.
    var sentAt = ++clock;
    var confirmed = null;
    try {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var result = await withTimeout(Promise.resolve().then(function () {
        return operation(controller && controller.signal);
      }), controller);
      if (options && options.cart) {
        try { confirmed = options.cart(result); } catch (error) { confirmed = null; }
      }
      return result;
    } finally {
      invalidate();
      mutations -= 1;
      // Overlapping mutations can land in any order, so only an unshared response is final.
      if (solo && !mutations && epoch === mutationEpoch && isCart(confirmed)) {
        last = { cart: confirmed, version: version, sentAt: sentAt };
        publish(confirmed, 'mutation');
      }
      if (!mutations) {
        mutationWaiters.splice(0).forEach(function (resolve) { resolve(); });
        setTimeout(function () {
          // Skip when a follow-up event (or consumer) already read this generation.
          if (!mutations && !pending && !(last && last.version === version)) refresh();
        }, SETTLE_DELAY);
      }
    }
  }

  window.theme.cartReads = {
    read: read, invalidate: invalidate, mutate: mutate,
    // Take before requesting cart HTML; pass as `since` to read() or as event detail.cartReadsSince.
    mark: function () { return ++clock; },
    request: function (url, options) {
      return mutate(function (signal) {
        return fetch(url, signal && !(options && options.signal) ? Object.assign({}, options, { signal: signal }) : options)
          .then(function (response) {
            // Buffer the body inside the timeout: a stalled body would otherwise hang callers' .json().
            if ([101, 204, 205, 304].indexOf(response.status) !== -1) return response;
            return response.arrayBuffer().then(function (body) {
              return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
            });
          });
      });
    },
    subscribe: function (listener) {
      listeners.add(listener);
      return function () { listeners.delete(listener); };
    }
  };
  ['cart:build', 'ajaxProduct:added', 'cart:drawer:refreshed', 'cart:updated'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      // HTML completion is a freshness boundary: apps can mutate without theme events.
      // Theme HTML carries the mark it was requested at; a read sent after it is fresh enough.
      var since = event.detail && event.detail.cartReadsSince;
      if (typeof since === 'number' && isFresh(since)) return;
      invalidate();
      refresh();
    }, true);
  });
  window.addEventListener('pageshow', function (event) {
    // Ordinary first paint already has Liquid's authoritative count. A back/forward load
    // without BFCache (e.g. in-app browsers) may render cached HTML, so revalidate it too.
    var navigation = window.performance && performance.getEntriesByType &&
      performance.getEntriesByType('navigation')[0];
    if (event.persisted || (navigation && navigation.type === 'back_forward')) { invalidate(); refresh(); }
  });
})();
